import { Button, Card, Center, Grid, Stack, Text, Title } from '@mantine/core';
import { IconChartBarOff, IconEdit } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import prettifiedLabel from '../../constants/labelPrettifier';
import { daysAgoISODate, dateToEndOfDay, dateToStartOfDay, todayISODate } from '../../helpers/dateUtils';
import { buildEmailFunnelSteps } from '../../helpers/funnelChart';
import useFetch from '../../helpers/useFetch';
import FunnelChart from '../Charts/FunnelChart';
import LineChart from '../Charts/LineChart';
import ErrorMessage from '../ErrorMessage';
import MetricsSelector from '../MetricsSelector';
import CarouselCard from '../ProductTrends/CarouselCard';
import Spinner from '../Spinner';

const statsDebounced = AwesomeDebouncePromise(
  (fetch, accountId, domainId, entityType, pageId, entityId, startTs, endTs) => {
    let pagePrefix = pageId ? `pages/${pageId}/` : '';
    return fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/${pagePrefix}${entityType}/${entityId}/stats/${encodeURIComponent(startTs)}/${encodeURIComponent(endTs)}?includeProducts=true`
    );
  },
  300
);

const transformProductsForCarousel = (products) => {
  if (!products || products.length === 0) return [];
  return products.map(p => ({
    _source: {
      id: p.id,
      name: p.name,
      imageUrl: p.imageUrl,
      listPrice: p.listPrice,
      discountPrice: p.discountPrice,
      currency: p.currency,
      available: p.available,
      // Add quantity and revenue as custom fields
      totalQuantity: p.totalQuantity,
      revenue: p.revenue
    },
    _score: p.purchaseCount,
    // Store product-specific metrics
    productMetrics: {
      quantity: Math.round(p.totalQuantity),
      revenue: p.revenue.toFixed(2)
    }
  }));
};

const DetailedStatistics = ({
  scope,
  pageId,
  id,
  startDate: controlledStartDate,
  endDate: controlledEndDate,
  setStartDate: controlledSetStartDate,
  setEndDate: controlledSetEndDate,
  hideMetricsSelector = false,
}) => {
  const { accountId, domainId } = useParams();
  const initialPeriodDays = 7;
  const fetch = useFetch();
  const location = useLocation();

  // Store ISO date strings (YYYY-MM-DD). When the parent controls the date range,
  // the internal state is unused — we always read from / write to the controlled props.
  const [internalStartDate, setInternalStartDate] = useState(daysAgoISODate(initialPeriodDays - 1));
  const [internalEndDate, setInternalEndDate] = useState(todayISODate());
  const startDate = controlledStartDate ?? internalStartDate;
  const endDate = controlledEndDate ?? internalEndDate;
  const setStartDate = controlledSetStartDate ?? setInternalStartDate;
  const setEndDate = controlledSetEndDate ?? setInternalEndDate;
  const [stats, setStats] = useState();
  const [error, setError] = useState();

  useEffect(() => {
    setStats(undefined);
    // Convert ISO date strings to full timestamps for the API (using local timezone)
    const startTs = dateToStartOfDay(startDate, 'local');
    const endTs = dateToEndOfDay(endDate, 'local');
    statsDebounced(fetch, accountId, domainId, scope, pageId, id, startTs, endTs)
      .then(({ statsByAction, dailyStatsByAction, productsPurchased }) =>
        setStats({
          // Transform to funnel format: the six email stages in flow order,
          // dropping any other action. FunnelChart draws the steps as given, so
          // this ordering is ours to do.
          funnelData: buildEmailFunnelSteps(
            Object.entries(statsByAction).map((e) => ({
              label: prettifiedLabel(e[0]),
              value: e[1].count,
            }))
          ),
          // Transform to line chart format: [{ id: 'Sent', data: [{ x: '2024-01-01', y: 10 }, ...] }, ...]
          lineChartData: Object.entries(dailyStatsByAction).map((e) => ({
            id: prettifiedLabel(e[0]),
            data: e[1],
          })),
          // Store products
          productsPurchased: productsPurchased || [],
        })
      )
      .catch(setError);
  }, [accountId, domainId, scope, pageId, id, fetch, startDate, endDate]);

  let content;
  if (error) {
    content = <ErrorMessage message={`There was an error loading the resource: ${error.message}`} />;
  } else if (stats) {
    content =
      stats.lineChartData.length > 0 ? (
        <LineChart data={stats.lineChartData} title="Unique profiles by event type">
          <FunnelChart data={stats.funnelData} height={250} />
        </LineChart>
      ) : (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconChartBarOff size={48} color="var(--mantine-color-dimmed)" stroke={1.5} />
            <Title c="dimmed" order={4} ta="center">
              No data available for the selected period
            </Title>
          </Stack>
        </Center>
      );
  } else {
    content = <Spinner />;
  }

  return (
    <>
      <Card padding="md">
        <Grid align="center" mb="md">
          <Grid.Col span={{ base: 12, md: 4 }}>
            {!hideMetricsSelector && (
              <MetricsSelector
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                rowSize="12"
              />
            )}
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }} />
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Button
              component="a"
              href={`#${location.pathname.replace('analytics', 'details')}`}
              size="xs"
              style={{ float: 'right' }}
            >
              <IconEdit size={14} /> Edit
            </Button>
          </Grid.Col>
        </Grid>
        {content}
      </Card>

      {stats?.productsPurchased && stats.productsPurchased.length > 0 && (
        <Card mt="md" padding="md" withBorder>
          <Card.Section inheritPadding py="sm">
            <Title order={4}>Products Purchased</Title>
            <Text size="sm" c="dimmed">
              Top products bought through this {scope.replace(/([A-Z])/g, ' $1').toLowerCase()}
            </Text>
          </Card.Section>
          <Card.Section inheritPadding py="sm">
            <CarouselCard
              hits={transformProductsForCarousel(stats.productsPurchased)}
              metrics={[
                { title: 'Purchases' }
              ]}
            />
          </Card.Section>
        </Card>
      )}
    </>
  );
};

export default DetailedStatistics;
